import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AutoCampaignConfigOverride,
  AutoCampaignRunNowRequest,
  EffectiveAutoCampaignConfig,
} from '@seo/types';
import { apiDelete, apiPatch, apiPost } from '@/lib/api';
import type {
  AutoCampaignInviteLinkResponse,
  AutoCampaignStatus,
  RunNowResponse,
} from './types';

/**
 * All mutations are grouped in a single file so we invalidate cache keys
 * consistently — status/history/backlog all share the same `['auto-campaign', …]`
 * prefix and every write should refresh at least the status card.
 */
function invalidateAll(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  void qc.invalidateQueries({ queryKey: ['auto-campaign', 'status', projectId] });
  void qc.invalidateQueries({ queryKey: ['auto-campaign', 'history', projectId] });
  void qc.invalidateQueries({ queryKey: ['auto-campaign', 'backlog', projectId] });
}

export function useEnableAutoCampaign(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<AutoCampaignStatus>(
        `/v1/projects/${projectId}/auto-campaign/enable`,
        {},
      ),
    onSuccess: () => invalidateAll(qc, projectId),
  });
}

export function useDisableAutoCampaign(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<AutoCampaignStatus>(
        `/v1/projects/${projectId}/auto-campaign/disable`,
        {},
      ),
    onSuccess: () => invalidateAll(qc, projectId),
  });
}

export function usePatchAutoCampaignConfig(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AutoCampaignConfigOverride) =>
      apiPatch<EffectiveAutoCampaignConfig>(
        `/v1/projects/${projectId}/auto-campaign/config`,
        body,
      ),
    onSuccess: () => invalidateAll(qc, projectId),
  });
}

export function useRunAutoCampaignNow(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AutoCampaignRunNowRequest) =>
      apiPost<RunNowResponse>(
        `/v1/projects/${projectId}/auto-campaign/run-now`,
        body,
      ),
    onSuccess: () => invalidateAll(qc, projectId),
  });
}

export function useDisableTelegramChat(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chatId: string) =>
      apiPost<{ project_id: string; chat_id: string; linked_chats_count: number }>(
        `/v1/projects/${projectId}/auto-campaign/chats/${chatId}/disable`,
        {},
      ),
    onSuccess: () => invalidateAll(qc, projectId),
  });
}

export function useAutoCampaignInviteLink(projectId: string) {
  return useMutation({
    mutationFn: (ttl_seconds?: number) =>
      apiPost<AutoCampaignInviteLinkResponse>(
        `/v1/projects/${projectId}/auto-campaign/invite-link`,
        ttl_seconds ? { ttl_seconds } : {},
      ),
  });
}

export function usePromoteBacklogCluster(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clusterId: string) =>
      apiPost<Record<string, unknown>>(
        `/v1/projects/${projectId}/auto-campaign/backlog/${clusterId}/promote`,
        {},
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['auto-campaign', 'backlog', projectId] }),
  });
}

export function useDemoteBacklogCluster(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clusterId: string) =>
      apiPost<Record<string, unknown>>(
        `/v1/projects/${projectId}/auto-campaign/backlog/${clusterId}/demote`,
        {},
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['auto-campaign', 'backlog', projectId] }),
  });
}

export function useDeleteBacklogCluster(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clusterId: string) =>
      apiDelete<Record<string, unknown>>(
        `/v1/projects/${projectId}/auto-campaign/backlog/${clusterId}`,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['auto-campaign', 'backlog', projectId] }),
  });
}

export function useResendDigest(campaignId: string) {
  return useMutation({
    mutationFn: () =>
      apiPost<{ queued: boolean; note: string }>(
        `/v1/campaigns/${campaignId}/resend-digest`,
        {},
      ),
  });
}
